import { useEffect, useState } from "react";
import "./App.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { HelmetProvider } from "react-helmet-async";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jwt_decode from "jwt-decode";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

//components
import Dashboard from "./components/dashboard/Dashboard";
import Login from "./components/Login";
import AllNotes from "./components/noteList/AllNotes";
import PageNotFound from "./components/PageNotFound";
import RecordPage from "./components/dashboard/Record/RecordPage";
import Settings from "./components/settings/Settings";
import HelpGuides from "./components/HelpGuides/HelpGuides";
import FHIRCallback from "./components/auth/FHIRCallback";
import PatientPage from "./components/VA/PatientPage";
import VAUnsopportedAccount from "./components/auth/VAUnsopportedAccount";
import StandaloneLanding from "./components/auth/StandaloneLanding";
import StandaloneCallback from "./components/auth/StandaloneCallback";
import StandaloneUnauthorizedAccount from "./components/auth/StandaloneUnauthorizedAccount";
import SessionExpired from "./components/auth/SessionExpired";
import VALaunchRequired, { VA_LAUNCH_REQUIRED_PATH } from "./components/auth/VALaunchRequired";
import { MacrosProvider } from "./utils/MacrosContext";
import { TutorialProvider } from "./components/Tutorial";
import TutorialManager from "./components/Tutorial/TutorialManager";
import LanguagePrompt from "./components/Onboarding/LanguagePrompt";
import SupportButton from "./components/support/SupportButton";
import RouteFocusManager from "./components/RouteFocusManager";
import LiveAnnouncer from "./components/LiveAnnouncer";
import ResourcesLaunch from "./components/pylon/ResourcesLaunch";

import { setUserId as setAmplitudeUserId } from "@amplitude/unified";
import SiteBanners from "./components/dashboard/NotificationBanner/SiteBanners";
import { clearAnalyticsContext, identifyUser } from "./lib/analytics";
import { isStandaloneUser as getIsStandaloneUser } from "./utils/standaloneUser";
import { PYLON_RESOURCES_LAUNCH_PATH } from "./utils/pylonKnowledgeBase";
import { getSessionToken } from "./lib/sessionToken";
import { announce } from "./lib/announce";
import {
  getPylonUnreadCount,
  setPylonHiddenFromAT,
  setPylonUnreadCount,
  unreadAnnouncement,
} from "./lib/pylonA11y";
import {
  clearSessionExpiredOnLoad,
  onSessionExpired,
  sessionExpiredOnLoad,
  SESSION_EXPIRED_PATH,
} from "./lib/sessionExpiry";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  // The idle timeout can end a session while the page loads, before this subscribes.
  // Read only: React StrictMode calls this initializer twice and keeps the second
  // result, so clearing the flag here would drop it in development.
  const [isSessionExpired, setIsSessionExpired] = useState(sessionExpiredOnLoad);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const setAuth = (boolean) => {
    if (boolean) setIsSessionExpired(false);
    setIsAuthenticated(boolean);
  };

  async function isAuth() {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/is-verify`, {
        method: "GET",
        headers: { token: getSessionToken() },
      });

      const parseRes = await response.json();
      // A request that rejected the JWT while this check was in flight has already
      // cleared the token, so a late "true" must not reopen the session.
      parseRes === true && getSessionToken()
        ? setIsAuthenticated(true)
        : setIsAuthenticated(false);

      // if user authorized, update the log
      const decoded_token = jwt_decode(getSessionToken());
      const user_id = decoded_token["user"]["id"];
      const user_email = decoded_token?.user?.email || decoded_token?.email || "";
      const pylonEmailHash = localStorage.getItem("pylonEmailHash") || "";
      if (user_email) {
        localStorage.setItem("userEmail", user_email);
      }

      // Amplitude
      setAmplitudeUserId(user_id);

      // pylon
      window.pylon = {
        chat_settings: {
          app_id: "6000e45a-6b53-4401-93b4-a8d408e9b062",
          ...(user_email ? { email: user_email } : {}),
          name: localStorage.getItem("userName") || "",
          ...(pylonEmailHash ? { email_hash: pylonEmailHash } : {}),
        },
      };
    } catch (err) {
      console.error("Authentication check failed:", err.message);
      // API down / network error is not a logout. Only clear auth when there
      // is no token to restore; otherwise leave isAuthenticated as null so
      // routes stay pending until the server is reachable again.
      if (!getSessionToken()) {
        setIsAuthenticated(false);
      }
    }
  }

  useEffect(() => {
    clearSessionExpiredOnLoad();
    isAuth();
  }, []);

  useEffect(
    () =>
      onSessionExpired(() => {
        // Cached queries belong to the account that just lost its session.
        queryClient.clear();
        setIsSessionExpired(true);
        setIsAuthenticated(false);
      }),
    []
  );

  useEffect(() => {
    let listenerRetryInterval = null;
    let visibilitySyncInterval = null;
    let visibilityObserver = null;
    let retryCount = 0;
    const maxRetries = 30;

    // The in-app <SupportButton /> is the only support launcher. Pylon's own
    // bubble is suppressed in index.css rather than through the JS API:
    // Pylon("show") always re-enables the bubble, and Pylon("hideChatBubble")
    // closes the chat window as a side effect.
    const handlePylonShow = () => {
      document.body.classList.add("pylon-chat-open");
      setPylonHiddenFromAT(false);
    };

    const handlePylonHide = () => {
      document.body.classList.remove("pylon-chat-open");
      setPylonHiddenFromAT(true);
    };

    // With the closed widget hidden from screen readers, replies would
    // otherwise arrive silently.
    const handleUnreadCountChange = (count) => {
      const previous = setPylonUnreadCount(count);
      if (document.body.classList.contains("pylon-chat-open")) return;
      const message = unreadAnnouncement(previous, getPylonUnreadCount());
      if (message) announce(message);
    };

    const isElementVisible = (element) => {
      if (!element) return false;
      const styles = window.getComputedStyle(element);
      if (
        styles.display === "none" ||
        styles.visibility === "hidden" ||
        styles.opacity === "0"
      ) {
        return false;
      }

      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    };

    const syncPylonClassesFromDom = () => {
      const chatWindow = document.querySelector(".PylonChat-chatWindowFrameContainer");
      const isOpen = isElementVisible(chatWindow);
      document.body.classList.toggle("pylon-chat-open", isOpen);
      setPylonHiddenFromAT(!isOpen);
    };

    const attachPylonListeners = () => {
      if (typeof window?.Pylon !== "function") {
        return false;
      }

      window.Pylon("onShow", handlePylonShow);
      window.Pylon("onHide", handlePylonHide);
      window.Pylon("onChangeUnreadMessagesCount", handleUnreadCountChange);
      return true;
    };

    if (!attachPylonListeners()) {
      listenerRetryInterval = setInterval(() => {
        retryCount += 1;
        const attached = attachPylonListeners();
        if (attached || retryCount >= maxRetries) {
          clearInterval(listenerRetryInterval);
        }
      }, 500);
    }

    // Fallback: track actual widget visibility to keep class in sync even if callbacks are flaky.
    visibilitySyncInterval = setInterval(syncPylonClassesFromDom, 250);
    visibilityObserver = new MutationObserver(syncPylonClassesFromDom);
    visibilityObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    syncPylonClassesFromDom();

    return () => {
      document.body.classList.remove("pylon-chat-open");
      if (listenerRetryInterval) {
        clearInterval(listenerRetryInterval);
      }
      if (visibilitySyncInterval) {
        clearInterval(visibilitySyncInterval);
      }
      if (visibilityObserver) {
        visibilityObserver.disconnect();
      }
      if (typeof window?.Pylon === "function") {
        window.Pylon("onShow", null);
        window.Pylon("onHide", null);
        window.Pylon("onChangeUnreadMessagesCount", null);
      }
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsSubscribed(false);
      return;
    }

    const fetchSubscriptionStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/user-info`, {
          method: "GET",
          headers: { token: getSessionToken() },
        });

        if (!response.ok) {
          setIsSubscribed(false);
          return;
        }

        const userData = await response.json();
        setIsSubscribed(Boolean(userData.is_subscribed));
      } catch (err) {
        console.error("Failed to fetch subscription status:", err.message);
        setIsSubscribed(false);
      }
    };

    fetchSubscriptionStatus();
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated === false) {
      clearAnalyticsContext();
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    const fetchAnalyticsIdentity = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/userinfo`, {
          method: "GET",
          headers: { token: getSessionToken() },
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        const user = Array.isArray(data) ? data[0] : null;

        if (!user) {
          return;
        }

        identifyUser({
          name: user.user_name,
          email: user.user_email,
          org: user.organization,
          super_org: user.super_org,
          specialty: user.specialty,
        });
      } catch (err) {
        console.error("Failed to fetch analytics user info:", err.message);
      }
    };

    fetchAnalyticsIdentity();
  }, [isAuthenticated]);

  const isStandaloneUser = getIsStandaloneUser();
  // No session and not expired means this tab never had one (new tab, restored tab,
  // bookmark). FHIRCallback sends genuinely unsupported accounts to /auth/va/unsupported
  // itself, so that page isn't the fallback here.
  const unauthenticatedRedirectPath = isSessionExpired
    ? SESSION_EXPIRED_PATH
    : isStandaloneUser
      ? "/standalone"
      : VA_LAUNCH_REQUIRED_PATH;
  // Keep routes mounted while auth is unknown so "/" / "/login" don't white-screen.
  const authPending = isAuthenticated === null;
  const requireAuth = (element) =>
    authPending ? null : isAuthenticated ? element : <Navigate to={unauthenticatedRedirectPath} />;
  const requireGuest = (element) =>
    authPending ? null : !isAuthenticated ? element : <Navigate to="/dashboard" />;
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <MacrosProvider>
          <Router>
            <a
              href="#main-content"
              className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[10000] focus:bg-white focus:text-accent1 focus:px-3 focus:py-2 focus:rounded"
              onClick={(e) => {
                e.preventDefault();
                const target = document.getElementById("main-content");
                if (target) {
                  target.focus();
                  target.scrollIntoView();
                }
              }}
            >
              Skip to main content
            </a>
            <RouteFocusManager />
            <TutorialProvider mode="onboarding" isSubscribed={isSubscribed}>
              <SiteBanners isAuthenticated={isAuthenticated} />
              <Routes>
                {/* Public FHIR callback - works for anyone */}
                <Route
                  exact
                  path="/auth/fhir/callback"
                  element={<FHIRCallback setAuth={setAuth} />}
                />
                <Route
                  exact
                  path="/standalone"
                  element={<StandaloneLanding />}
                />
                <Route
                  exact
                  path="/auth/standalone"
                  element={<StandaloneCallback setAuth={setAuth} />}
                />
                <Route
                  exact
                  path="/auth/standalone/unauthorized"
                  element={<StandaloneUnauthorizedAccount />}
                />
                <Route exact path="/auth/va/unsupported" element={<VAUnsopportedAccount />} />
                <Route exact path={VA_LAUNCH_REQUIRED_PATH} element={<VALaunchRequired />} />
                <Route exact path={SESSION_EXPIRED_PATH} element={<SessionExpired />} />
                {/* <Route
            exact
            path="/test/va-oauth"
            element={<VAOAuthTest />}
          /> */}
                <Route
                  exact
                  path="/login"
                  element={requireGuest(
                    isStandaloneUser ? (
                      <Navigate to="/standalone" replace />
                    ) : (
                      <Login setAuth={setAuth} />
                    )
                  )}
                />
                <Route
                  exact
                  path="/dashboard"
                  element={requireAuth(<Dashboard setAuth={setAuth} />)}
                />
                <Route
                  exact
                  path="/notes"
                  element={requireAuth(<AllNotes setAuth={setAuth} />)}
                />
                <Route
                  exact
                  path="/record/:patientId"
                  element={requireAuth(<RecordPage setAuth={setAuth} />)}
                />
                <Route
                  exact
                  path="/"
                  element={
                    authPending ? null : isAuthenticated ? (
                      <Navigate to="/dashboard" />
                    ) : (
                      <Navigate to={unauthenticatedRedirectPath} />
                    )
                  }
                />
                <Route
                  exact
                  path="/settings"
                  element={requireAuth(<Settings setAuth={setAuth} />)}
                />
                <Route
                  exact
                  path="/help-guides"
                  element={requireAuth(<HelpGuides setAuth={setAuth} />)}
                />
                <Route
                  exact
                  path={PYLON_RESOURCES_LAUNCH_PATH}
                  element={requireAuth(<ResourcesLaunch />)}
                />
                <Route
                  path="/patient/:patient_id"
                  element={<PatientPage setAuth={setAuth} />}
                />
                <Route exact path="*" element={authPending ? null : <PageNotFound />} />
              </Routes>
              {isSubscribed && <TutorialManager />}
              <SupportButton />
              {/* Unmounting on sign-out resets the prompt's per-session state. */}
              {isAuthenticated && isSubscribed && <LanguagePrompt />}
            </TutorialProvider>
          </Router>
          <div>
            {/* LiveAnnouncer voices every toast, so the toasts' own alert role would double it. */}
            <ToastContainer role="none" />
          </div>
          <LiveAnnouncer />
        </MacrosProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
